import { memo, useEffect, useState, useCallback, useRef } from "react";
import Form from "../../components/Form";
import {
    getOwners,
    getAnimals,
    getAnimalSupervisionData,
    getAnimalSupervisionMilkRecordings,
} from "../../api/index.js";
import { useSnackbar } from "../../utils/index.js";
import useSelectStyles from "../../components/styles/Select.js";
import useListStyles from "../../components/styles/List.js";
import Spinner from "../../components/Spinner.js";
import { Typography, Box, Grid2, Button } from "@mui/material";
import Table from "../../components/Table.js";
import MilkingPeriodSummary from "./MilkingPeriodSummary.js";
import {
    registrationCodeHeaderNoClick,
    flockCodeHeader,
    sexHeaderNoFilter,
    breedCodeHeader,
    birthDateHeaderNotFilterable,
    regDateHeader,
    updatedAtHeader,
    ownerHeaderNoClick,
} from "../../components/TableHeaders.js";

const ReadOnlyCell = ({ value }) => (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="body2">{value ?? "-"}</Typography>
    </Box>
);

const DateCell = ({ value }) => (
    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
        <Typography variant="body2">
            {value ? new Date(value).toLocaleDateString("el-GR") : "-"}
        </Typography>
    </Box>
);

const Supervision = () => {
    const [isLoading, setIsLoading] = useState(false);
    const [fetchedOwners, setFetchedOwners] = useState([]);
    const [ownersDropdownValues, setOwnersDropdownValues] = useState([]);
    const [animalData, setAnimalData] = useState(null);
    const [fetchedAnimals, setFetchedAnimals] = useState([]);
    const [animalsDropdownValues, setAnimalsDropdownValues] = useState([]);
    const [selectedAnimalId, setSelectedAnimalId] = useState(null);
    const [selectedLambing, setSelectedLambing] = useState(null);
    const [milkRecordings, setMilkRecordings] = useState([]);
    const [milkingSummary, setMilkingSummary] = useState(null);
    const [step, setStep] = useState(1);
    const milkRequestId = useRef(0);

    const classes = useSelectStyles();
    const listClasses = useListStyles();
    const { error } = useSnackbar();

    const fetchData = useCallback(
        async () => {
            setIsLoading(true);
            try {
                const { owners } = await getOwners();
                setFetchedOwners(owners);

                const dropdownValues = owners.map((owner) => ({
                    value: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`,
                    text: `${owner?.lastName} ${owner?.firstName} - ${owner?.code}`
                }));
                setOwnersDropdownValues(dropdownValues);
            } catch (err) {
                error(err);
            }
            setIsLoading(false);
        }
    )

    useEffect(() => {
        fetchData();
    }, []);

    const animalTableColumns = [
        registrationCodeHeaderNoClick,
        flockCodeHeader,
        sexHeaderNoFilter,
        breedCodeHeader,
        birthDateHeaderNotFilterable,
        regDateHeader,
        updatedAtHeader,
    ]

    const animalParentTableColuns = [
        ownerHeaderNoClick,
        flockCodeHeader,
        registrationCodeHeaderNoClick
    ]

    const lambingTableColumns = [{
        Header: <Typography variant="h8">Ημ/νία Τοκετού</Typography>,
        accessor: "lambingDate",
        id: "lambingDate",
        filterable: false,
        minWidth: 230,
        Cell: ({ value, row }) => (
            <Button
                variant={selectedLambing?._id === row._original?._id ? "contained" : "text"}
                color="secondary"
                onClick={() => handleLambingClick(row._original)}
                sx={{ textTransform: "none" }}
            >
                {new Date(value).toLocaleDateString("el-GR")}
            </Button>
        ),
    }];

    const milkRecordingTableColumns = [
        {
            Header: <Typography variant="h8">Ημερομηνία Ελέγχου</Typography>,
            accessor: "testDate",
            id: "testDate",
            filterable: false,
            minWidth: 180,
            Cell: DateCell,
        },
        {
            Header: <Typography variant="h8">Πρωινή Μέτρηση (gr)</Typography>,
            accessor: "morningMilk",
            id: "morningMilk",
            filterable: false,
            minWidth: 180,
            Cell: ReadOnlyCell,
        },
        {
            Header: <Typography variant="h8">Μεσημεριανή Μέτρηση (gr)</Typography>,
            accessor: "noonMilk",
            id: "noonMilk",
            filterable: false,
            minWidth: 200,
            Cell: ReadOnlyCell,
        },
        {
            Header: <Typography variant="h8">Βραδινή Μέτρηση (gr)</Typography>,
            accessor: "eveningMilk",
            id: "eveningMilk",
            filterable: false,
            minWidth: 180,
            Cell: ReadOnlyCell,
        },
        {
            Header: <Typography variant="h8">Σύνολο (gr)</Typography>,
            accessor: "totalMilk",
            id: "totalMilk",
            filterable: false,
            minWidth: 140,
            Cell: ReadOnlyCell,
        },
    ];

    const ownerFormContent = [
        {
            customType: "dropdown",
            id: "owner",
            items: ownersDropdownValues,
            placeholder: "Επιλογή εκτροφής",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Συνέχεια",
        },
    ]

    const handleOwnerSubmit = async (values) => {
        setIsLoading(true);
        milkRequestId.current += 1;
        setAnimalData(null);
        setSelectedAnimalId(null);
        setSelectedLambing(null);
        setMilkRecordings([]);
        setMilkingSummary(null);
        const ownerId = fetchedOwners.find((owner) => `${owner?.lastName} ${owner?.firstName} - ${owner?.code}` === values.owner)?._id;
        const { success: scs, animals, message } = await getAnimals(ownerId);
        if (scs) {
            setFetchedAnimals(animals);
            setAnimalsDropdownValues(animals.map((animal) => ({
                value: `${animal.registrationCode}`,
                text: `${animal.registrationCode}`
            })));
            setStep(2);
        } else {
            error(message);
        }
        setIsLoading(false);
    }

    const animalFormContent = [
        {
            customType: "dropdown",
            id: "animal",
            items: animalsDropdownValues,
            placeholder: "Επιλογή Ζώου",
            required: true,
        },
        {
            customType: "button",
            id: "submit",
            type: "submit",
            text: "Επιλογή",
        },
    ]

    const handleSubmitAnimalForm = async (values) => {
        setIsLoading(true);
        milkRequestId.current += 1;
        setSelectedLambing(null);
        setMilkRecordings([]);
        setMilkingSummary(null);
        const selectedAnimalId = fetchedAnimals.find((an) => an.registrationCode === values.animal)._id;
        const { success: scs, data, message } = await getAnimalSupervisionData(selectedAnimalId);
        if (scs) {
            setSelectedAnimalId(selectedAnimalId);
            setAnimalData(data);
        } else {
            error(message);
        }
        setIsLoading(false);
    }

    async function handleLambingClick(lambing) {
        if (!selectedAnimalId || !lambing?._id) return;

        const requestId = milkRequestId.current + 1;
        milkRequestId.current = requestId;
        setSelectedLambing(lambing);
        setMilkRecordings([]);
        setMilkingSummary(null);
        setIsLoading(true);

        try {
            const { success: scs, milkRecordings: recordings, milkingSummary: summary, message } = await getAnimalSupervisionMilkRecordings(
                selectedAnimalId,
                lambing._id,
            );

            if (requestId !== milkRequestId.current) return;

            if (scs) {
                setMilkRecordings(recordings || []);
                setMilkingSummary(summary || null);
            } else {
                setSelectedLambing(null);
                error(message);
            }
        } catch (err) {
            if (requestId === milkRequestId.current) {
                setSelectedLambing(null);
                error(err);
            }
        } finally {
            if (requestId === milkRequestId.current) setIsLoading(false);
        }
    }
    return (
        <>
            <Spinner open={isLoading} />
            {step === 1 && (
                <>
                    <Box className={classes.outerBox}>
                        <Box container flexDirection="column" className={classes.innerBox}>

                            <Typography variant="h5" sx={{ margin: "1rem" }}>
                                {"Επιλογή εκτροφής"}
                            </Typography>
                            <Form
                                content={ownerFormContent}
                                onSubmit={handleOwnerSubmit}
                                toResetForm={false}
                            />
                        </Box>
                    </Box>
                </>
            )}
            {step === 2 && (
                <>
                    <Form
                        content={animalFormContent}
                        onSubmit={handleSubmitAnimalForm}
                        toResetForm={false}
                    />
                    <Box sx={{ display: "flex", justifyContent: "center" }}>
                        <Button
                            onClick={() => setStep(step - 1)}
                            sx={{
                                marginTop: "1rem",
                                padding: "0.5rem 1rem",
                                backgroundColor: "third",
                                color: "secondary.main",
                                border: "none",
                                borderRadius: "4px",
                                cursor: "pointer",
                                variant: "h5",
                                display: "flex",
                                justifyContent: "center",
                            }}
                        >
                            Πίσω
                        </Button>
                    </Box>
                    {animalData && (
                        <>
                            <Grid2 container spacing={2} sx={{ marginTop: "1rem" }}>
                                <Grid2 item xs={12} md={12}>
                                    <Typography variant="h6" className={listClasses.form_title}>
                                        {"Πληροφορίες Προβάτου"}
                                    </Typography>
                                    <Table
                                        data={[animalData?.animalData]}
                                        noDataText={isLoading ? "Φόρτωση..." : "Δεν υπάρχουν πληροφορίες για το πρόβατο"}
                                        columns={animalTableColumns}
                                        showPageSizeOptions={false}
                                    />
                                </Grid2>

                                <Grid2 item xs={12} md={6}>
                                    <Typography variant="h6" className={listClasses.form_title}>
                                        {"Πληροφορίες Μητέρας"}
                                    </Typography>
                                    <Table
                                        data={[animalData?.dam]}
                                        noDataText={isLoading ? "Φόρτωση..." : "Δεν υπάρχουν πληροφορίες για τη Μητέρα"}
                                        columns={animalParentTableColuns}
                                        showPageSizeOptions={false}
                                    />
                                </Grid2>

                                <Grid2 item xs={12} md={6}>
                                    <Typography variant="h6" className={listClasses.form_title}>
                                        {"Πληροφορίες Πατέρα"}
                                    </Typography>
                                    <Table
                                        data={[animalData?.sire]}
                                        noDataText={isLoading ? "Φόρτωση..." : "Δεν υπάρχουν πληροφορίες για τον Πατέρα"}
                                        columns={animalParentTableColuns}
                                        showPageSizeOptions={false}
                                    />
                                </Grid2>

                            </Grid2>
                            <Box sx={{ width: "90%", mt: "1rem" }}>
                                <Typography variant="h6" className={listClasses.form_title}>
                                    {"Τοκετοί"}
                                </Typography>
                            </Box>
                            <Grid2
                                container
                                spacing={2}
                                sx={{ width: "calc(100% - 2rem)", mx: "1rem" }}
                            >
                                <Grid2 size={{ xs: 12, md: selectedLambing ? 4 : 12 }}>
                                    <Table
                                        data={animalData?.lambing || []}
                                        noDataText={isLoading ? "Φόρτωση..." : "Δεν υπάρχουν καταχωρημένοι τοκετοί"}
                                        columns={lambingTableColumns}
                                        showPageSizeOptions={false}
                                    />
                                </Grid2>

                                {selectedLambing && (
                                    <Grid2 size={{ xs: 12, md: 8 }}>
                                        <Typography variant="h6" className={listClasses.form_title}>
                                            {`Γαλακτομετρήσεις περιόδου ${new Date(selectedLambing.lambingDate).toLocaleDateString("el-GR")}`}
                                        </Typography>
                                        <MilkingPeriodSummary summary={milkingSummary} />
                                        <Box sx={{ width: "100%", overflowX: "auto" }}>
                                            <Table
                                                data={milkRecordings}
                                                noDataText={isLoading ? "Φόρτωση..." : "Δεν υπάρχουν γαλακτομετρήσεις για αυτή την περίοδο"}
                                                columns={milkRecordingTableColumns}
                                                showPageSizeOptions={false}
                                                showPagination={false}
                                                page={0}
                                                pageSize={Math.max(milkRecordings.length, 1)}
                                            />
                                        </Box>
                                    </Grid2>
                                )}
                            </Grid2>
                        </>
                    )}
                </>
            )
            }
        </>
    )
}

export default memo(Supervision)
